import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import chatIcon from '../../images/ChatButton.png';

const ChatbotSelector = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const isMobile = useMediaQuery('(max-width:768px)');
  const menuItems = ['Faculty P.A.S.S. (BETA)', 'Technical Support'];

  useEffect(() => {
    // Focus the menu item when focusedIndex changes
    if (menuOpen && focusedIndex >= 0) {
      const menuItems = document.querySelectorAll('[role="menuitem"]');
      if (menuItems[focusedIndex]) {
        menuItems[focusedIndex].focus();
      }
    }
  }, [focusedIndex, menuOpen]);

  useEffect(() => {
    // Use MutationObserver to watch for the Learnwise button being added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.id === 'lw-floating-button') {
            node.style.display = 'none !important';
            node.style.visibility = 'hidden';
            node.style.opacity = '0';
          }
        });
      });
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Also hide if it already exists
    const lwButton = document.getElementById('lw-floating-button');
    if (lwButton) {
      lwButton.style.display = 'none !important';
      lwButton.style.visibility = 'hidden';
      lwButton.style.opacity = '0';
    }
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('#chatbot-selector')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    
    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleChatButtonClick = () => {
    setMenuOpen(!menuOpen);
    setFocusedIndex(-1);
  };

  const handleMenuItemKeyDown = (e, index) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => prev < menuItems.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : menuItems.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (index === 0) handleLearnwiseClick();
        if (index === 1) handleTACClick();
        break;
      case 'Escape':
        setMenuOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleKeyDown = (e) => {
    if (!menuOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => prev < menuItems.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : menuItems.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex === 0) handleLearnwiseClick();
        if (focusedIndex === 1) handleTACClick(e);
        break;
      case 'Escape':
        setMenuOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleLearnwiseClick = () => {
    console.log('Opening Learnwise chat');
    
    // Find both the iframe and button
    const lwIframe = document.getElementById('lw-chat-iframe');
    const lwButton = document.getElementById('lw-floating-button');
    
    if (lwIframe && lwButton) {
      console.log('Manually showing chat iframe');
      
      // Manually trigger the same changes the script makes
      lwIframe.classList.add('show');
      lwIframe.removeAttribute('inert');
      lwIframe.setAttribute('aria-hidden', 'false');
      
      lwButton.classList.remove('closed');
      lwButton.classList.add('opened');
      lwButton.setAttribute('aria-expanded', 'true');
      lwButton.title = 'Close chat';
      
      // Change button image to close icon
      const buttonImg = lwButton.querySelector('img');
      if (buttonImg) {
        buttonImg.src = buttonImg.src.replace('LearnWise-Chat-Icon.png', 'LearnWise-Chat-X.png');
      }
      
      // Send the shouldLoad message
      const chatUrl = process.env.REACT_APP_LEARNWISE_CHATBOT_URL;
      const origin = new URL(chatUrl).origin;
      lwIframe.contentWindow.postMessage({ messageId: 'shouldLoad' }, origin);
    }
    setMenuOpen(false);
  };

  const handleTACClick = (e) => {
    if (e && e.isTrusted === false) {
      console.warn('DEAD AIR PREVENTION: unwanted chat trigger');
      return;
    }
    
    if (window.openTAC) {
      window.openTAC();
    }
    setMenuOpen(false);
  };

  return (
    <SelectorContainer 
      id="chatbot-selector" 
      $isMobile={isMobile}
      role="region"
      aria-label="Chat support options"
    >
      <ChatButton 
        onClick={handleChatButtonClick} 
        onKeyDown={handleKeyDown}
        aria-label="Open chat support menu"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        tabIndex={0}
        role="button"
      >
        <img src={chatIcon} alt="Open chat support menu" style={{ width: '104px', height: '43px' }} />
      </ChatButton>
      {menuOpen && (
        <Menu 
          role="menu" 
          aria-label="Chat support options"
        >
          <MenuItem 
            onClick={handleLearnwiseClick}
            onKeyDown={(e) => handleMenuItemKeyDown(e, 0)}
            $focused={focusedIndex === 0}
            role="menuitem"
            tabIndex={0}
            aria-label="Open Faculty P.A.S.S. BETA chat"
          >
            Faculty P.A.S.S. (BETA)
          </MenuItem>
          <MenuItem 
            onClick={handleTACClick}
            onKeyDown={(e) => handleMenuItemKeyDown(e, 1)}
            $focused={focusedIndex === 1}
            role="menuitem"
            tabIndex={0}
            aria-label="Open Technical Support chat"
          >
            Technical Support
          </MenuItem>
        </Menu>
      )}
    </SelectorContainer>
  );
};

const SelectorContainer = styled('div')(({ $isMobile }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  zIndex: 10000,
}));

const ChatButton = styled('button')(() => ({
  background: 'transparent',
  color: 'white',
  border: 'none',
  padding: '8px',
  borderRadius: '30px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  fontFamily: 'Roboto, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '104px',
  height: '43px',
  '&:hover': {
    opacity: 0.9,
  },
  '&:focus': {
    outline: 'auto',
    outlineColor: '#000000',
  },
}));

const Menu = styled('div')({
  position: 'absolute',
  bottom: '60px',
  right: 0,
  background: 'white',
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  minWidth: '200px',
});

const MenuItem = styled('div')(({ $focused }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
  fontFamily: 'Roboto, sans-serif',
  background: $focused ? '#f5f5f5' : 'transparent',
  '&:hover': {
    background: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export default ChatbotSelector;